import { useFieldArray, type ArrayPath, type FieldArrayWithId, type FieldValues, type Path, type UseFormReturn } from 'react-hook-form';
import { CornerDownRight, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DecimalInput } from '@/components/forms/DecimalInput';

/** The items slice shared by the invoice and quotation forms. */
export interface LineItemFormValue {
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  children: Array<{ description: string }>;
}

interface LineItemsEditorProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  fields: FieldArrayWithId<T, ArrayPath<T>, 'id'>[];
  remove: (index: number) => void;
  lumpSum: boolean;
  disabled?: boolean;
}

/**
 * Line items for a priced document. Each line can carry description-only
 * sub-points ("+ Sub-point"), which print indented beneath it and never
 * affect the totals.
 */
export function LineItemsEditor<T extends FieldValues>({ form, fields, remove, lumpSum, disabled }: LineItemsEditorProps<T>) {
  return (
    <>
      {fields.map((field, index) => (
        <LineItemRow
          key={field.id}
          form={form}
          index={index}
          lumpSum={lumpSum}
          disabled={disabled}
          canRemove={fields.length > 1}
          onRemove={() => remove(index)}
        />
      ))}
    </>
  );
}

function LineItemRow<T extends FieldValues>({ form, index, lumpSum, disabled, canRemove, onRemove }: {
  form: UseFormReturn<T>;
  index: number;
  lumpSum: boolean;
  disabled?: boolean;
  canRemove: boolean;
  onRemove: () => void;
}) {
  // Each line owns its sub-point array; a nested field array needs its own hook.
  const children = useFieldArray({ control: form.control, name: `items.${index}.children` as ArrayPath<T> });
  const path = (suffix: string) => `items.${index}.${suffix}` as Path<T>;

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-4">
        <div className="flex-1"><Label>Description</Label><Input {...form.register(path('description'))} disabled={disabled} /></div>
        {!lumpSum && (
          <>
            <div className="w-20"><Label>Qty</Label><DecimalInput {...form.register(path('quantity'))} disabled={disabled} /></div>
            <div className="w-28"><Label>Price</Label><DecimalInput {...form.register(path('unitPrice'))} disabled={disabled} /></div>
          </>
        )}
        <Button type="button" variant="ghost" size="icon" onClick={onRemove} disabled={disabled || !canRemove} aria-label="Remove line">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {children.fields.map((child, childIndex) => (
        <div key={child.id} className="ml-4 flex items-center gap-2">
          <CornerDownRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Input
            className="h-8 text-sm"
            placeholder="Sub-point — printed under this line, no price"
            {...form.register(path(`children.${childIndex}.description`))}
            disabled={disabled}
          />
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => children.remove(childIndex)} disabled={disabled} aria-label="Remove sub-point">
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="ml-4 h-7 px-2 text-xs text-muted-foreground"
        onClick={() => children.append({ description: '' } as never)}
        disabled={disabled}
      >
        <Plus className="mr-1 h-3 w-3" />Sub-point
      </Button>
    </div>
  );
}
